import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { useContextStore } from '@store/context'
import { useOperateStore } from '@/store/operate'
import { isConsoleScopedInstance } from '@/config/runtime'
import { isOrganizationPath } from '@/components/shell/shellNavTreeState'

/**
 * Stable org picker for the header. Application / Agent selection lives in the
 * left sidebar tree (Run | Studio), not in this control.
 */
export default function ContextSwitcher() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const organizations = useContextStore((state) => state.organizations)
  const org = useContextStore((state) => state.org)
  const isLoading = useContextStore((state) => state.isLoading)
  const error = useContextStore((state) => state.error)
  const setCurrentSessionId = useOperateStore((state) => state.setCurrentSessionId)
  const setOrg = useContextStore((state) => state.setOrg)
  const setWorkspace = useContextStore((state) => state.setWorkspace)
  const scopedInstance = isConsoleScopedInstance()

  const selectedOrganization = organizations.find((item) => item.id === org)

  const handleOrgChange = (nextOrg: string) => {
    setOrg(nextOrg || null)
    setWorkspace(null)
    setCurrentSessionId(null)
    if (location.pathname.startsWith('/run')) {
      navigate('/run/chat')
    } else if (isOrganizationPath(location.pathname)) {
      // Organisation leaves stay put: they read the new organization.
      navigate(location.pathname)
    } else if (
      location.pathname.startsWith('/studio') ||
      location.pathname.startsWith('/compose')
    ) {
      // Agent and Application leaves belong to the previous organization.
      navigate('/studio/chat')
    } else {
      navigate(location.pathname)
    }
    setOpen(false)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex h-10 max-w-[18rem] items-center gap-3 rounded-full border border-input bg-card px-4 text-sm text-foreground transition-colors hover:bg-card-raised"
        data-testid="context-switcher-toggle"
      >
        <span className="min-w-0 truncate font-semibold">
          {selectedOrganization?.name || t('contextSwitcher.selectOrganization')}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-2 w-80 space-y-3 rounded-card border border-border bg-popover p-3">
          <div className="space-y-1">
            <label className="text-[13px] font-semibold text-foreground">
              {t('contextSwitcher.organization')}
            </label>
            {scopedInstance ? (
              <div className="w-full rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-foreground">
                {selectedOrganization?.name || t('contextSwitcher.notSelected')}
              </div>
            ) : (
              <select
                value={org || ''}
                onChange={(e) => handleOrgChange(e.target.value)}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                data-testid="organization-select"
              >
                <option value="">{t('contextSwitcher.selectOrganization')}</option>
                {organizations.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            <div>
              {t('contextSwitcher.orgId')}: {org || t('contextSwitcher.notSelected')}
            </div>
          </div>

          {isLoading && (
            <div className="text-xs text-muted-foreground">
              {t('contextSwitcher.loadingContext')}
            </div>
          )}

          {error && <div className="text-xs text-error">{error}</div>}
        </div>
      )}
    </div>
  )
}
